import FolderOutlined from "@mui/icons-material/FolderOutlined";
import PermMediaOutlined from "@mui/icons-material/PermMediaOutlined";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useFolders } from "./hooks/use-folders";
import {
  flattenTreeItems,
  folderNameFromId,
  ROOT_ID,
  transformFoldersToTreeItems,
  type UiFolderId,
} from "./utils";

type AssetFolderSelectProps = {
  selectedFolderId: UiFolderId;
  onChange: (folderId: UiFolderId) => void;
};

export const AssetFolderSelect: FC<AssetFolderSelectProps> = ({ selectedFolderId, onChange }) => {
  const { t } = useTranslation();
  const { data: folders, loading, error } = useFolders();
  const treeItems = useMemo(() => transformFoldersToTreeItems(folders ?? []), [folders]);
  const options = useMemo(() => flattenTreeItems(treeItems), [treeItems]);
  const selectedLabel = folderNameFromId(selectedFolderId, folders ?? null) ?? t("Unknown folder");
  const menuOptions = useMemo(() => {
    if (options.some((option) => option.id === selectedFolderId)) return options;
    return [{ id: selectedFolderId, label: selectedLabel, depth: 0 }, ...options];
  }, [options, selectedFolderId, selectedLabel]);

  return (
    <TextField
      select
      label={t("Save to folder")}
      value={selectedFolderId}
      onChange={(event) => onChange(event.target.value as UiFolderId)}
      disabled={loading || Boolean(error)}
      error={Boolean(error)}
      helperText={error ? t("Unable to load folders") : " "}
      slotProps={{
        select: {
          renderValue: () => selectedLabel,
          MenuProps: {
            PaperProps: {
              sx: { maxHeight: 360 },
            },
          },
        },
      }}
    >
      {menuOptions.map((option) => {
        const isRoot = option.id === ROOT_ID;
        return (
          <MenuItem key={option.id} value={option.id}>
            <Box display="flex" alignItems="center" gap={1} sx={{ pl: option.depth * 2 }}>
              {isRoot ? (
                <PermMediaOutlined fontSize="small" />
              ) : (
                <FolderOutlined fontSize="small" />
              )}
              <Typography variant="body2" noWrap>
                {option.label}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}
    </TextField>
  );
};
